import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import http, { businesses as bizApi, auction as auctionApi } from '../services/api';
import { Search, Gavel, Award, Store, MapPin, Clock, Star, ArrowRight, Play, Sparkles, Users, HeartHandshake } from 'lucide-react';
import PsaCard from '../components/PsaCard';
import AppImage from '../components/AppImage';
import BusinessLogoAvatar from '../components/BusinessLogoAvatar';
import HomepageBannerSlider from '../components/HomepageBannerSlider';
import HomePopupBanner from '../components/HomePopupBanner';
import CommunityTeaser from '../components/CommunityTeaser';
import GoldenMap from '../components/GoldenMap';
import PilotFundingSection from '../components/PilotFundingSection';
import SiteSections from '../components/SiteSections';
import { HomeNewsHeadlines } from '../components/HomeNewsHeadlines';

const HERO_IMG = `${process.env.REACT_APP_BACKEND_URL}/api/assets/hero.png`;
const POSTER_BG = `${process.env.REACT_APP_BACKEND_URL}/api/assets/poster-bg.png`;

function videoEmbed(url) {
  if (!url) return null;
  // Facebook Reels / Videos
  const fb = url.match(/facebook\.com\/(?:reel\/(\d+)|watch\/?\?v=(\d+)|[^/]+\/videos\/(\d+))/);
  if (fb || url.includes('facebook.com/')) {
    const src = `https://www.facebook.com/plugins/video.php?height=308&href=${encodeURIComponent(url)}&show_text=false&width=560&t=0`;
    return { kind: 'facebook', src };
  }
  // YouTube
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  if (yt) return { kind: 'youtube', src: `https://www.youtube.com/embed/${yt[1]}?rel=0` };
  return null;
}

function timeLeft(ends) {
  if (!ends) return '';
  const diff = new Date(ends) - Date.now();
  if (diff <= 0) return 'Ended';
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (d > 0) return `${d}d ${h}h left`;
  if (h > 0) return `${h}h ${m}m left`;
  return `${m}m left`;
}

export default function PublicHome() {
  const [cfg, setCfg] = useState(null);
  const [biz, setBiz] = useState([]);
  const [auctions, setAuctions] = useState([]);

  useEffect(() => {
    http.get('/config/public').then(r => setCfg(r.data)).catch(() => setCfg({}));
    bizApi.list().then(setBiz).catch(() => {});
    auctionApi.list().then(setAuctions).catch(() => {});
  }, []);

  const featured = useMemo(() => biz.slice(0, 6), [biz]);
  const live = useMemo(() => auctions.filter(a => a.status === 'active').slice(0, 6), [auctions]);
  const video = videoEmbed(cfg?.videoUrl || cfg?.youtubeUrl);

  return (
    <>
      <HomePopupBanner />

      {/* GOLDEN MAP — Phase 3 of the Blackout Game. Lives at the top so every
          visitor sees the live network at a glance. */}
      <section className="bg-neutral-950 text-white py-10 border-b border-neutral-900">
        <div className="max-w-6xl mx-auto px-4">
          <GoldenMap />
        </div>
      </section>

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="max-w-5xl mx-auto px-4 py-12 md:py-16 text-center">
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-green-800 bg-green-50 border border-green-200 rounded-full px-3 py-1"><Sparkles size={11}/> Community · Commerce · Impact</div>

          {/* Vibrant urban banner replaces the scripted heading — same slot, richer palette */}
          <div
            data-testid="hero-heading-frame"
            className="relative mt-6 rounded-3xl overflow-hidden shadow-2xl border border-green-900/20"
            style={{
              backgroundImage: `radial-gradient(ellipse at top left, rgba(34,197,94,0.35), transparent 55%), radial-gradient(ellipse at bottom right, rgba(251,191,36,0.40), transparent 55%), linear-gradient(135deg, #065f46 0%, #14532d 55%, #064e3b 100%)`,
              boxShadow: '0 30px 80px -20px rgba(0,0,0,0.35), inset 0 0 120px rgba(0,0,0,0.10)',
            }}
          >
            <img
              data-testid="hero-heading"
              src={`${process.env.REACT_APP_BACKEND_URL}/api/assets/banner.png`}
              alt="Promote Black Business — Bringing Business and Communities Together"
              className="w-full h-auto block"
              onError={(e) => { e.currentTarget.style.display='none'; }}
            />
          </div>

          <HomeNewsHeadlines />

          {/* Hero — dynamic banner slider (Full Package businesses) with a
              graceful fallback to the static image below when there are none. */}
          <div className="relative mt-8 md:mt-10 max-w-3xl mx-auto">
            <HomepageBannerSlider />
            {/* Fallback static image renders below only if slider produces no items */}
            <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-white bg-neutral-100 aspect-[16/9] mt-4 hidden" data-testid="hero-fallback-image">
              <img src={HERO_IMG} alt="Community and Black-owned business" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display='none'; }}/>
            </div>
            <div className="absolute -bottom-4 -right-4 bg-white rounded-2xl shadow-xl border border-neutral-200 px-4 py-3 hidden md:flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center"><Award className="text-green-700" size={20}/></div>
              <div className="text-left">
                <div className="font-semibold text-sm">Earn rewards every visit</div>
                <div className="text-[11px] text-neutral-500">10 stamps → +100 pts bonus</div>
              </div>
            </div>
          </div>

          {/* Subtext BELOW the image */}
          <p className="mt-10 text-lg md:text-xl text-neutral-700 max-w-2xl mx-auto leading-relaxed">
            {cfg?.heroSubtext || 'Discover Black-owned businesses, earn rewards, and fuel your community — one purchase at a time.'}
          </p>

          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Link to="/plans" data-testid="hero-cta-plans" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-green-500 hover:bg-green-400 text-black font-bold shadow-lg shadow-green-500/20">
              <Sparkles size={16}/> Choose a Plan
            </Link>
            <Link to="/directory" data-testid="hero-cta-directory" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-green-600 hover:bg-green-700 text-white font-semibold">
              <Search size={16}/> Browse the Directory
            </Link>
            <Link to="/signup" data-testid="hero-cta-signup" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-neutral-900 hover:bg-neutral-800 text-white font-semibold">
              Register Free <ArrowRight size={16}/>
            </Link>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-6 text-xs text-neutral-500 justify-center">
            <div className="inline-flex items-center gap-1"><Users size={12}/> Be one of our first 100 members — register free today</div>
            <div className="inline-flex items-center gap-1"><HeartHandshake size={12}/> Fueling C.A.N. — Community Auction Network</div>
          </div>
        </div>
      </section>

      {/* PSA — "Why Join The Movement?" (placed between hero CTAs and the video) */}
      <PsaCard />

      {/* VIDEO */}
      {video && (
        <section className="max-w-4xl mx-auto px-4 py-8" data-testid="video-section">
          <div className={`rounded-2xl overflow-hidden bg-black shadow-xl border border-neutral-200 ${video.kind === 'facebook' ? 'aspect-[9/16] max-w-sm mx-auto md:max-w-md' : 'aspect-video'}`}>
            <iframe title="Featured video" src={video.src} width="100%" height="100%"
              style={{ border: 'none', overflow: 'hidden' }} scrolling="no" frameBorder="0"
              allowFullScreen
              allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
              className="w-full h-full"/>
          </div>
          {video.kind === 'facebook' && (
            <div className="text-center mt-2 text-xs text-neutral-500">Featured reel · Follow us on Facebook</div>
          )}
        </section>
      )}

      {/* FEATURE CARDS */}
      <section className="max-w-7xl mx-auto px-4 py-12" data-testid="features-section">
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { icon: Store, title: 'Explore Businesses', body: 'Search a growing directory of verified Black-owned businesses across every category — food, beauty, tech, home services and more.', to: '/directory' },
            { icon: Gavel, title: 'Bid on Auctions', body: 'Bid points and cash on donated items in the Community Auction Network (C.A.N.) auction — every bid fuels youth and community programs.', to: '/auction' },
            { icon: Award, title: 'Earn Rewards', body: 'Every scan, review, and purchase adds a stamp. Fill your loyalty card to auto-earn +100 pts — free to any member.', to: '/loyalty' },
          ].map((f, i) => (
            <Link key={f.title} to={f.to} data-testid={`feature-card-${i}`} className="group rounded-2xl bg-white border border-neutral-200 p-6 hover:border-green-500 hover:shadow-lg transition-all">
              <div className="w-11 h-11 rounded-xl bg-green-100 flex items-center justify-center text-green-700 group-hover:bg-green-600 group-hover:text-white transition-colors">
                <f.icon size={22}/>
              </div>
              <div className="mt-4 font-bold text-lg">{f.title}</div>
              <p className="mt-1 text-sm text-neutral-600">{f.body}</p>
              <div className="mt-3 inline-flex items-center gap-1 text-sm text-green-700 font-semibold">Learn more <ArrowRight size={14}/></div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED BUSINESSES */}
      <section className="max-w-7xl mx-auto px-4 py-12 border-t border-neutral-200" data-testid="businesses-section">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h2 className="text-2xl md:text-3xl font-black tracking-tight">Featured Businesses</h2>
            <p className="text-sm text-neutral-600 mt-1">Fresh picks from the community.</p>
          </div>
          <Link to="/directory" className="text-sm font-semibold text-green-700 hover:underline hidden sm:inline-flex items-center gap-1">See all <ArrowRight size={14}/></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {featured.length === 0 ? (
            <div className="col-span-full rounded-3xl border-2 border-dashed border-green-300 bg-green-50/60 p-8 md:p-12 text-center" data-testid="biz-empty-state">
              <Store className="mx-auto text-green-700" size={28}/>
              <h3 className="mt-3 text-lg md:text-xl font-black text-neutral-900">Be one of the first 100 businesses listed</h3>
              <p className="mt-2 text-sm text-neutral-600 max-w-md mx-auto">Register now and get <span className="font-semibold text-green-700">founding member status</span> — priority placement, permanent recognition, and early access to loyalty tools.</p>
              <Link to="/signup" className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-green-600 hover:bg-green-700 text-white text-sm font-semibold">Claim Founding Spot <ArrowRight size={14}/></Link>
            </div>
          ) : featured.map(b => (
            <Link key={b.id} to={`/business/${b.id}`} data-testid={`biz-preview-${b.id}`} className="group rounded-2xl bg-white border border-neutral-200 overflow-hidden hover:shadow-lg hover:border-green-500 transition-all">
              <div className="relative">
                <AppImage src={b.image} alt={b.name} rounded="none" imgClassName="group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute -bottom-6 left-4 z-10">
                  <BusinessLogoAvatar business={b} size="md" />
                </div>
              </div>
              <div className="p-4 pt-8">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-bold truncate text-sm md:text-base">{b.name}</div>
                  {b.tier && <span className="shrink-0 text-[10px] uppercase font-bold bg-green-100 text-green-800 px-2 py-0.5 rounded-full">{b.tier}</span>}
                </div>
                <div className="text-[11px] text-neutral-500 mt-1 truncate">{b.category}</div>
                {b.city && <div className="text-[11px] text-neutral-500 mt-0.5 inline-flex items-center gap-1"><MapPin size={10}/> {b.city}</div>}
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* LIVE AUCTIONS — always render section so early-activation state is visible */}
      <section className="max-w-7xl mx-auto px-4 py-12 border-t border-neutral-200" data-testid="auctions-section">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h2 className="text-2xl md:text-3xl font-black tracking-tight inline-flex items-center gap-2"><Gavel size={22} className="text-green-700"/> Live Auctions</h2>
            <p className="text-sm text-neutral-600 mt-1">Bid to lift up youth + community programs.</p>
          </div>
          <Link to="/auction" className="text-sm font-semibold text-green-700 hover:underline hidden sm:inline-flex items-center gap-1">Enter auction <ArrowRight size={14}/></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {live.length === 0 ? (
            <div className="col-span-full rounded-3xl border-2 border-dashed border-green-300 bg-green-50/60 p-8 md:p-12 text-center" data-testid="auction-preview-empty">
              <Gavel className="mx-auto text-green-700" size={28}/>
              <h3 className="mt-3 text-lg md:text-xl font-black text-neutral-900">Be one of the first 100 bidders</h3>
              <p className="mt-2 text-sm text-neutral-600 max-w-md mx-auto">Auctions launching soon. <span className="font-semibold text-green-700">Register now to claim your spot</span> — early bidders get founding member perks when the C.A.N. Auction Store opens.</p>
              <Link to="/signup" className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-green-600 hover:bg-green-700 text-white text-sm font-semibold">Register Free <ArrowRight size={14}/></Link>
            </div>
          ) : live.map(a => (
            <Link key={a.id} to="/auction" data-testid={`auction-preview-${a.id}`} className="group rounded-2xl bg-white border border-neutral-200 overflow-hidden hover:shadow-lg hover:border-green-500 transition-all">
              <div className="aspect-[16/10] bg-neutral-100 overflow-hidden">
                {a.image ? <img src={a.image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/> : <div className="w-full h-full flex items-center justify-center text-neutral-300"><Gavel size={26}/></div>}
              </div>
              <div className="p-4">
                <div className="font-bold text-sm md:text-base truncate">{a.title}</div>
                <div className="mt-1 flex items-center justify-between text-xs">
                  <span className="text-green-700 font-bold">{a.currentBid || a.startingBid || 0} pts</span>
                  <span className="text-neutral-500 inline-flex items-center gap-1"><Clock size={10}/> {timeLeft(a.endsAt)}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="mt-8" data-testid="cta-section">
        <div className="max-w-5xl mx-auto px-4">
          <div className="rounded-3xl bg-gradient-to-br from-neutral-900 to-neutral-800 text-white p-8 md:p-12 text-center shadow-2xl relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-green-500/20 blur-3xl"/>
            <h2 className="text-3xl md:text-4xl font-black relative">Ready to join the community?</h2>
            <p className="mt-2 text-neutral-300 max-w-xl mx-auto relative">Sign up free in 30 seconds — start earning points, save on coupons, and support Black business every day.</p>
            <div className="mt-6 flex flex-wrap gap-3 justify-center relative">
              <Link to="/plans" data-testid="cta-plans" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-green-500 hover:bg-green-400 text-black font-bold shadow-xl">
                <Sparkles size={16}/> Choose a Plan
              </Link>
              <Link to="/signup" data-testid="cta-signup" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white hover:bg-neutral-100 text-neutral-900 font-bold shadow-xl">
                Register Free <ArrowRight size={16}/>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Admin-editable content injected here */}
      <SiteSections pageKey="home" />

      {/* Community teaser (public — always visible) */}
      <CommunityTeaser />

      {/* Pilot funding + countdown + donate */}
      <PilotFundingSection />
    </>
  );
}
